import { useState } from "react";
import { Check, Coins } from "lucide-react";

import { useTranslation } from "@/i18n";
import { CURRENCIES, CURRENCY_SYMBOL, useCurrency, type CurrencyCode } from "@/lib/currency/use-currency";
import { CurrencyDisclaimer } from "@/lib/currency/price";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const CURRENCY_LABEL_KEY: Record<CurrencyCode, string> = {
  GBP: "currencies.gbp",
  HKD: "currencies.hkd",
  JPY: "currencies.jpy",
  USD: "currencies.usd",
  EUR: "currencies.eur",
};

const CURRENCY_DEFAULT_LABEL: Record<CurrencyCode, string> = {
  GBP: "British Pound",
  HKD: "Hong Kong Dollar",
  JPY: "Japanese Yen",
  USD: "US Dollar",
  EUR: "Euro",
};

export function CurrencySelector({
  variant = "header",
  className,
}: {
  variant?: "header" | "footer";
  className?: string;
}) {
  const { t } = useTranslation("currency");
  const { currency, setCurrency } = useCurrency();
  const [open, setOpen] = useState(false);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant === "header" ? "outline" : "ghost"}
          size="sm"
          aria-label={t("changeCurrency", "Change display currency")}
          className={cn(
            "gap-2 rounded-xl",
            variant === "header" &&
              "h-10 px-3 border-white/10 bg-white/5 backdrop-blur-xl hover:border-white/20 hover:bg-white/10",
            variant === "footer" && "h-9 px-3 text-foreground/80",
            className,
          )}
        >
          <Coins className="h-4 w-4" aria-hidden="true" />
          <span className="text-sm">{CURRENCY_SYMBOL[currency]} {currency}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-64 rounded-xl border-white/10 bg-background/90 backdrop-blur-xl"
      >
        {CURRENCIES.map((code) => (
          <DropdownMenuItem
            key={code}
            onSelect={(e) => {
              e.preventDefault();
              setOpen(false);
              setCurrency(code);
            }}
            aria-current={code === currency ? "true" : undefined}
            className="flex items-center justify-between gap-2"
          >
            <span>
              {CURRENCY_SYMBOL[code]} {code} · {t(CURRENCY_LABEL_KEY[code], CURRENCY_DEFAULT_LABEL[code])}
            </span>
            {code === currency && <Check className="h-4 w-4 text-primary" aria-hidden="true" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <div className="px-2 py-2">
          <CurrencyDisclaimer />
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
